import {
  GenericGridItem,
  InfoIcon,
  WbBadge,
  WbCardContent,
  WbGenericFieldGrid,
  WbWidget,
  WbInfoTooltip,
} from '@agilelab/plugin-wb-platform';
import GroupIcon from '@material-ui/icons/Group';
import PersonIcon from '@material-ui/icons/Person';
import { TeamRoleConfig } from '@agilelab/plugin-wb-team-roles-common';
import { Box, capitalize, makeStyles, Typography } from '@material-ui/core';
import { CSSProperties, FC, ReactNode } from 'react';

const useStyles = makeStyles(
  theme => ({
    flexBox: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  }),
  { name: 'TeamRoleCard' },
);

interface TeamRoleCardProps {
  teamRole: TeamRoleConfig;
  title: ReactNode;
  labelInfo?: string;
  style?: CSSProperties;
}

export const TeamRoleCard: FC<TeamRoleCardProps> = ({
  teamRole,
  title,
  labelInfo,
  style,
}) => {
  const classes = useStyles();

  const defaultAssigneeLimitedAssigneeKind: string = 'User or Group';
  const assigneeKind: string =
    teamRole.assigneeKind ?? defaultAssigneeLimitedAssigneeKind;

  const limitedAssigneeKind: string =
    teamRole.limitedAssigneeKind ?? `${assigneeKind} (Inherited)`;

  const fields: GenericGridItem[] = [
    {
      label: 'Full Assignment RBAC Role',
      colSpan: 2,
      value: (
        <WbBadge key={teamRole.assigneeRbacRole}>
          {teamRole.assigneeRbacRole}
        </WbBadge>
      ),
      labelInfo:
        'Any user assigned to this Team Role will be granted the specified RBAC role.',
    },
    teamRole.limitedAssigneeRbacRole
      ? {
          label: 'Limited Assignment RBAC Role',
          colSpan: 2,
          value: (
            <WbBadge key={teamRole.limitedAssigneeRbacRole}>
              {teamRole.limitedAssigneeRbacRole}
            </WbBadge>
          ),
          labelInfo:
            'Any user assigned as Limited to this Team Role will be granted the specified RBAC role.',
        }
      : {
          label: 'Limited Assignment RBAC Role',
          colSpan: 2,
          value: 'Limited assignments are disabled',
          icon: 'no_access',
          labelInfo:
            'Limited assignments are disabled for this Team Role. Check the documentation for more details on how to enable limited assignments.',
        },
    {
      label: 'Full Assignment Subject Type',
      value: (
        <Box className={classes.flexBox}>
          {assigneeKind === 'user' ? (
            <PersonIcon fontSize="small" />
          ) : (
            <GroupIcon fontSize="small" />
          )}
          <Typography variant="body1" style={{ fontWeight: '500' }}>
            {capitalize(assigneeKind)}
          </Typography>
        </Box>
      ),
      colSpan: 2,
      labelInfo:
        'Enforces whether this Team Role must be assigned to a user or a group. As of now, only user assignment is supported.',
    },
  ];

  if (teamRole.limitedAssigneeRbacRole) {
    fields.push({
      label: 'Limited Assignment Subject Type',
      value: (
        <Box className={classes.flexBox}>
          {limitedAssigneeKind === 'user' ? (
            <PersonIcon fontSize="small" />
          ) : (
            <GroupIcon fontSize="small" />
          )}
          <Typography variant="body1" style={{ fontWeight: '500' }}>
            {capitalize(limitedAssigneeKind)}
          </Typography>
        </Box>
      ),
      colSpan: 2,
      labelInfo:
        'Enforces whether this Team Role must be assigned to a user or a group. As of now, only user assignment is supported.',
    });
  }

  return (
    <div style={style}>
      <WbWidget
        title={title}
        icon={
          labelInfo ? (
            <WbInfoTooltip title={labelInfo} icon={<InfoIcon />} />
          ) : undefined
        }
      >
        <WbCardContent>
          <WbGenericFieldGrid fields={fields} />
        </WbCardContent>
      </WbWidget>
    </div>
  );
};
